import React from 'react';

import {
  defaultInitialsFieldHeightPx,
  defaultInitialsFieldWidthPx,
  defaultSignatureFieldHeightPx,
  defaultSignatureFieldWidthPx,
} from '@aline/common';
import { SquarePen } from 'lucide-react';
import { getSignaturePlaceholderLabel } from 'src/helpers/signature-labels';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<{ isSelected: boolean }>()((theme, { isSelected }) => ({
  content: {
    '& > svg': {
      flexShrink: 0,
      height: 24,
      width: 24,
    },
    alignItems: 'center',
    backgroundColor: theme.appColors.defaultFieldBackground,
    border: `${isSelected ? 2 : 1}px dashed ${theme.palette.primary.main}`,
    borderRadius: '3px',
    boxSizing: 'border-box',
    color: theme.appColors.iconsType,
    display: 'flex',
    fontSize: 18,
    gap: 8,
    justifyContent: 'center',
    // Gives back the extra border width when selected, so the icon and text stay put.
    padding: isSelected ? 7 : 8,
    transformOrigin: 'top left',
  },
  label: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface SignatureEmptyStateProps {
  isInitials: boolean;
  isSelected: boolean;
  isSigning: boolean;
  /** Rendered width over the variant's default width. */
  scale: number;
}

/**
 * Dashed "Click to sign" box of an unsigned signature/initials field. Laid out at the
 * variant's default size and scaled, so the icon and text shrink with the field.
 */
export function SignatureEmptyState({
  isInitials,
  isSelected,
  isSigning,
  scale,
}: SignatureEmptyStateProps) {
  const { classes } = useStyles({ isSelected });

  const style: React.CSSProperties = {
    height: isInitials ? defaultInitialsFieldHeightPx : defaultSignatureFieldHeightPx,
    transform: `scale(${scale})`,
    width: isInitials ? defaultInitialsFieldWidthPx : defaultSignatureFieldWidthPx,
  };

  return (
    <div className={classes.content} style={style}>
      <SquarePen />
      <span className={classes.label}>
        {getSignaturePlaceholderLabel({ isInitials, isSigning })}
      </span>
    </div>
  );
}
